import { lazy, Suspense, useEffect } from 'react';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { WsProvider } from '@/contexts/WsContext';
import { FeatureAccessProvider } from '@/contexts/FeatureAccessContext';
import { CurrencyProvider } from '@/contexts/CurrencyContext';
import { EntitlementProvider } from '@/contexts/EntitlementContext';
import EntitlementModal from '@/components/entitlement/EntitlementModal';
import { PartnerConsentProvider } from '@/contexts/PartnerConsentContext';
import PartnerConsentModal from '@/components/partner-consent/PartnerConsentModal';
import { FeatureGate } from '@/components/FeatureGate';
import ErrorBoundary from '@/components/ErrorBoundary';
import ScrollToTop from '@/components/ScrollToTop';
import { ConfirmProvider } from '@/components/ConfirmDialog';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import Layout from '@/components/Layout';
import { SEOHead } from '@/components/SEOHead';
import { lazyWithPreload } from '@/lib/lazyWithPreload';
import { isDashboardPath, primeDashboard } from '@/lib/dashboardPrime';
// The landing page is the one page that is NOT lazy. It is the first thing a
// new visitor sees, and a lazy route cannot be on screen sooner than ~300 ms
// after its Suspense fallback appeared (React throttles that swap). About
// 9 KB gzip in the main bundle buys the logged-out homepage a first frame
// with no placeholder; AuthContext (probeCanSignIn) is what lets that frame
// be drawn without waiting for the auth check.
import LandingPage from '@/pages/LandingPage';
const PortalSelectorPage = lazy(() => import('@/pages/PortalSelectorPage'));
const AuthPage = lazy(() => import('@/pages/AuthPage'));
const OnboardingPage = lazy(() => import('@/pages/OnboardingPage'));
// The dashboard is where a signed-in person lands, so its code is asked for
// before it is needed (see lazyWithPreload): at startup below, and from
// AppRoutes while somebody is signing in or finishing onboarding.
const DashboardPage = lazyWithPreload(() => import('@/pages/DashboardPage'));
const MedicationsPage = lazy(() => import('@/pages/MedicationsPage'));
const ReportsPage = lazy(() => import('@/pages/ReportsPage'));
const CarersPage = lazy(() => import('@/pages/CarersPage'));
const SchedulingPage = lazy(() => import('@/pages/SchedulingPage'));
const ProfilePage = lazy(() => import('@/pages/ProfilePage'));
const ChatPage = lazy(() => import('@/pages/ChatPage'));
const PlansPage = lazy(() => import('@/pages/PlansPage'));
const BillingPage = lazy(() => import('@/pages/BillingPage'));
const SubscribeCheckoutPage = lazy(() => import('@/pages/SubscribeCheckoutPage'));
const SubscribeSuccessPage = lazy(() => import('@/pages/SubscribeSuccessPage'));
const AlaCartCheckoutPage = lazy(() => import('@/pages/AlaCartCheckoutPage'));
const AlaCartSuccessPage = lazy(() => import('@/pages/AlaCartSuccessPage'));
const AppointmentsPage = lazy(() => import('@/pages/AppointmentsPage'));
const AdminDashboardPage = lazy(() => import('@/pages/AdminDashboardPage'));
const HeatmapPage = lazy(() => import('@/pages/HeatmapPage'));
const CarerAdminPage = lazy(() => import('@/pages/CarerAdminPage'));
const ConsultationPage = lazy(() => import('@/pages/ConsultationsPage'));
const PropertyPage = lazy(() => import('@/pages/PropertiesPage'));
const HealthTrendsPage = lazy(() => import('@/pages/HealthTrendsPage'));
const ElderlyPortalPage = lazy(() => import('@/pages/ElderlyPortalPage'));
const CSDashboardPage = lazy(() => import('@/pages/CSDashboardPage'));
const PMDashboardPage = lazy(() => import('@/pages/PMDashboardPage'));
const DataControlPage = lazy(() => import('@/pages/DataControlPage'));
const ReferralPage = lazy(() => import('@/pages/ReferralPage'));
const InsurancePage = lazy(() => import('@/pages/InsurancePage'));
const TaxesPage = lazy(() => import('@/pages/TaxesPage'));
const AboutPage = lazy(() => import('@/pages/AboutPage'));
const TermsPage = lazy(() => import('@/pages/TermsPage'));
const PrivacyPolicyPage = lazy(() => import('@/pages/PrivacyPolicyPage'));
const BlogPage = lazy(() => import('@/pages/BlogPage'));
const BlogArticlePage = lazy(() => import('@/pages/BlogArticlePage'));
const CookiePolicyPage = lazy(() => import('@/pages/CookiePolicyPage'));
const SurveyPage = lazy(() => import('@/pages/SurveyPage'));
const ConsentManagementPage = lazy(() => import('@/pages/ConsentManagementPage'));
const HealthGraphPage = lazy(() => import('@/pages/HealthGraphPage'));
const HealthPassportPage = lazy(() => import('@/pages/HealthPassportPage'));
const PassportPublicPage = lazy(() => import('@/pages/PassportPublicPage'));
const MonthlyReportPage = lazy(() => import('@/pages/MonthlyReportPage'));
const AITrainingPage = lazy(() => import('@/pages/AITrainingPage'));
const CSCheckinPage = lazy(() => import('@/pages/CSCheckinPage'));
const MedicineDeliveryV01Page = lazy(() => import('@/pages/MedicineDeliveryV01Page'));
const CarerRequestV01Page = lazy(() => import('@/pages/CarerRequestV01Page'));
const DiagnosticsV01Page = lazy(() => import('@/pages/DiagnosticsV01Page'));
const PropertyRequestV01Page = lazy(() => import('@/pages/PropertyRequestV01Page'));
const MyRequestsPage = lazy(() => import('@/pages/MyRequestsPage'));
const NutritionistRequestV01Page = lazy(() => import('@/pages/NutritionistRequestV01Page'));
const DoctorConsultationV01Page = lazy(() => import('@/pages/DoctorConsultationV01Page'));
const NutritionistProfilePage = lazy(() => import('@/pages/NutritionistProfilePage'));
const WalletPage = lazy(() => import('@/pages/WalletPage'));
const AdminRefundsPage = lazy(() => import('@/pages/AdminRefundsPage'));
const AdminConsultsPage = lazy(() => import('@/pages/AdminConsultsPage'));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));
const NRIChat = lazy(() => import('@/features/chat/NRIChat'));
const ElderlyChat = lazy(() => import('@/features/chat/ElderlyChat'));

// A page load on the dashboard's address with a stored token: fetch the
// dashboard's code now, alongside the sign-in check, not after it.
try {
  if (isDashboardPath(window.location.pathname) && localStorage.getItem('token')) DashboardPage.preload();
} catch {
  // Storage blocked — the page is fetched when it is first drawn.
}

import { InstallPrompt } from '@/components/InstallPrompt';
import { CookieConsent } from '@/components/CookieConsent';
import { usePageTracking } from '@/hooks/usePageTracking';
import { Toaster } from 'sonner';
import { X, ArrowRight, Clock } from 'lucide-react';

function UpgradeModal() {
  const { upgradeModal, setUpgradeModal } = useAuth();
  const navigate = useNavigate();

  if (!upgradeModal) return null;
  const { is403, comingSoonMessage, serviceName } = upgradeModal;
  const close = () => setUpgradeModal(null);

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={close}>
      <div className="nsofar-card p-6 w-full max-w-md animate-fade-in-up relative" onClick={e => e.stopPropagation()}>
        <button className="absolute top-3 right-3 p-1 rounded-lg hover:bg-[var(--hover-surface)]" onClick={close}>
          <X size={18} style={{ color: 'var(--text-muted)' }} />
        </button>

        {is403 ? (
          <div className="text-center">
            <div className="w-14 h-14 rounded-2xl mx-auto mb-4 flex items-center justify-center" style={{ background: 'var(--brand-primary-light)' }}>
              <Clock size={24} style={{ color: 'var(--brand-primary)' }} />
            </div>
            <h3 className="text-lg font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Coming Soon</h3>
            <p className="text-sm mb-4" style={{ color: 'var(--text-muted)' }}>{comingSoonMessage}</p>
            <button className="nsofar-btn nsofar-btn-primary text-sm" onClick={close}>Got it</button>
          </div>
        ) : (
          <>
            {/* Section 1: Upgrade */}
            <h3 className="text-lg font-bold mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Upgrade Your Plan</h3>
            <p className="text-sm mb-4" style={{ color: 'var(--text-muted)' }}>{serviceName}</p>
            <button className="nsofar-btn nsofar-btn-primary w-full text-sm mb-4 flex items-center justify-center gap-2"
              onClick={() => { close(); navigate('/plans'); }}>
              View Plans <ArrowRight size={16} />
            </button>

            {/* Section 2: Contact link */}
            <div className="text-center border-t pt-3" style={{ borderColor: 'var(--border-color)' }}>
              <a href={`https://wa.me/?text=${encodeURIComponent("Hi, I'd like one-time access to a nsofar service")}`}
                target="_blank" rel="noopener noreferrer"
                className="text-xs hover:underline" style={{ color: 'var(--text-muted)' }}>
                Or contact us for one-time access
              </a>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function AppRoutes() {
  const { user, loading, api, getToken, addSignInGate } = useAuth();
  const { pathname, search } = useLocation();
  usePageTracking();

  // The dashboard's data, asked for as early as its code is (see
  // lib/dashboardPrime). On a page load: alongside the sign-in check.
  useEffect(() => {
    if (loading && isDashboardPath(window.location.pathname)) primeDashboard(api, getToken());
    // Mount only: this is about the sign-in check that is in flight right now.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // On a sign-in that lands on the dashboard: the moment the token is known,
  // while the sign-in is still being completed. Returns nothing, so it does
  // not hold the sign-in up.
  useEffect(() => addSignInGate((signingIn) => {
    if (getHomeRoute(signingIn) !== '/') return;
    DashboardPage.preload();
    primeDashboard(api, getToken());
  }), [addSignInGate, api, getToken]);

  // The family sign-in form and onboarding both end on the dashboard.
  useEffect(() => {
    const role = new URLSearchParams(search).get('role') || 'user';
    if ((pathname === '/auth' && role === 'user') || pathname === '/onboarding') DashboardPage.preload();
  }, [pathname, search]);

  if (loading) return <div style={{ visibility: 'hidden', minHeight: '100vh' }} />;

  return (
  <>
    {/* Suspense boundary for the lazy-loaded route chunks (code-splitting).
        Fallback mirrors the auth-loading placeholder above — a full-height
        empty div — so a fast chunk load causes no spinner flash or layout
        shift; a slow one just holds the frame until the chunk arrives. */}
    <Suspense fallback={<div aria-busy="true" style={{ minHeight: '100vh' }} />}>
    <Routes>
      {/* Portal Selector - always accessible.
          The redirects here, on /auth and on /onboarding use `replace`. A
          pushed redirect leaves the redirecting address in the history:
          Back lands on it and is sent forward again, or stays on a blank
          page if the page it was redirected to has not appeared yet. */}
      <Route path="/portal" element={user ? <Navigate to={getHomeRoute(user)} replace /> : <PortalSelectorPage />} />

      {/* Elderly Portal - separate auth flow.
          `.elderly-theme` pins a warm, always-light palette for this surface:
          the portal paints cream cards but reads text from the shared tokens,
          so the account holder's dark mode used to turn its text near-white on
          near-white. Wrapping at the route keeps any future elderly component
          covered without each one remembering to opt out. */}
      <Route path="/elderly" element={<div className="elderly-theme"><SEOHead route="/elderly" /><ElderlyPortalPage /></div>} />
      <Route path="/elderly/chat" element={<div className="elderly-theme"><SEOHead route="/elderly" path="/elderly/chat" /><ElderlyChat /></div>} />

      {/* Auth Pages - role specific */}
      <Route path="/auth" element={user ? <Navigate to={getHomeRoute(user)} replace /> : <AuthPage />} />

      {/* Public Health Passport - no login required. The share token in the
          URL is the only credential, so the page must never be indexed (the
          X-Robots-Tag in public/_headers is the JS-independent half). */}
      <Route path="/passport/:shareToken" element={<><SEOHead title="Shared Health Passport | nsofar" noindex /><PassportPublicPage /></>} />

      {/* Legal pages - open to everyone, signed in or not. Registered only
          for signed-out visitors they showed a signed-in user "Page not
          found", and the cookie banner sends signed-in users to /cookies. */}
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/cookies" element={<CookiePolicyPage />} />

      {/* About - open to everyone too. It is a public page with its own
          address in search results, so a signed-in user who follows one, or
          a shared link, must get the page and not "Page not found". */}
      <Route path="/about" element={<AboutPage />} />

      {/* Onboarding - users only */}
      <Route path="/onboarding" element={user ? <OnboardingPage /> : <Navigate to="/portal" replace />} />

      {/* Authenticated routes */}
      {user ? (
        <>
          {/* User routes */}
          <Route path="/" element={<Layout><DashboardPage /></Layout>} />
          <Route path="/dashboard" element={<Layout><DashboardPage /></Layout>} />
          <Route path="/health-trends" element={<Layout><FeatureGate featureKey="health_trends"><HealthTrendsPage /></FeatureGate></Layout>} />
          <Route path="/medications" element={<Layout><FeatureGate featureKey="medications"><MedicationsPage /></FeatureGate></Layout>} />
          <Route path="/reports" element={<Layout><FeatureGate featureKey="health_reports"><ReportsPage /></FeatureGate></Layout>} />
          <Route path="/carers" element={<Layout><FeatureGate featureKey="carers"><CarersPage /></FeatureGate></Layout>} />
          <Route path="/scheduling" element={<Layout><FeatureGate featureKey="carers"><SchedulingPage /></FeatureGate></Layout>} />
          <Route path="/appointments" element={<Layout><FeatureGate featureKey="appointments"><AppointmentsPage /></FeatureGate></Layout>} />
          {/* docs/32 v0.1 flow only (legacy tracker retired 2026-07-08) — gated
              by the same toggle as the /doctor-consultation booking page. */}
          <Route path="/consultations" element={<Layout><FeatureGate featureKey="doctor_consultation_v01"><ConsultationPage /></FeatureGate></Layout>} />
          <Route path="/properties" element={<Layout><FeatureGate featureKey="properties"><PropertyPage /></FeatureGate></Layout>} />
          <Route path="/chat" element={<Layout><FeatureGate featureKey="chat"><ChatPage /></FeatureGate></Layout>} />
          <Route path="/ai-chat" element={<Layout><FeatureGate featureKey="chat"><NRIChat /></FeatureGate></Layout>} />
          {/* /plans is the upgrade destination. showUnlessDisabled: reachable for
              any logged-in user even when plan-gated or "coming soon" (a free
              user must be able to open the page that lets them upgrade — no
              catch-22), but BLOCKED ("Not Available") when an admin disables the
              "Subscription Plans" feature — so disabling it hides the page from
              EVERY entry point (direct URL, entitlement-modal CTA, profile links,
              checkout back-buttons, etc.), not just the nav link. */}
          <Route path="/plans" element={<Layout><FeatureGate featureKey="plans" showUnlessDisabled><PlansPage /></FeatureGate></Layout>} />
          {/* Cluster C.2 — embedded Stripe Elements subscribe flow. No Layout
              wrapper (full-page checkout); no FeatureGate (PlansPage gates the CTA). */}
          <Route path="/subscribe/checkout" element={<SubscribeCheckoutPage />} />
          <Route path="/subscribe/success" element={<SubscribeSuccessPage />} />
          {/* Cluster C.4 — à la carte embedded Stripe Elements flow. */}
          <Route path="/alacarte/checkout" element={<AlaCartCheckoutPage />} />
          <Route path="/alacarte/success" element={<AlaCartSuccessPage />} />
          <Route path="/profile" element={<Layout><ProfilePage /></Layout>} />
          <Route path="/billing" element={<Layout><BillingPage /></Layout>} />
          <Route path="/referrals" element={<Layout><ReferralPage /></Layout>} />
          <Route path="/insurance" element={<Layout><FeatureGate featureKey="insurance"><InsurancePage /></FeatureGate></Layout>} />
          <Route path="/taxes" element={<Layout><FeatureGate featureKey="taxes"><TaxesPage /></FeatureGate></Layout>} />
          <Route path="/data-control" element={<Layout><DataControlPage /></Layout>} />
          <Route path="/settings/privacy" element={<Layout><ConsentManagementPage /></Layout>} />
          <Route path="/health-graph" element={<Layout><HealthGraphPage /></Layout>} />
          <Route path="/health-passport" element={<Layout><HealthPassportPage /></Layout>} />
          <Route path="/monthly-reports" element={<Layout><MonthlyReportPage /></Layout>} />
          <Route path="/medicine-delivery" element={<Layout><MedicineDeliveryV01Page /></Layout>} />
          <Route path="/carer-request" element={<Layout><CarerRequestV01Page /></Layout>} />
          <Route path="/diagnostics" element={<Layout><DiagnosticsV01Page /></Layout>} />
          <Route path="/property-request" element={<Layout><PropertyRequestV01Page /></Layout>} />
          <Route path="/doctor-consultation" element={<Layout><FeatureGate featureKey="doctor_consultation_v01"><DoctorConsultationV01Page /></FeatureGate></Layout>} />
          <Route path="/nutritionist" element={<Layout><FeatureGate featureKey="nutritionist_v01"><NutritionistRequestV01Page /></FeatureGate></Layout>} />
          <Route path="/nutritionist/profile/:requestId" element={<Layout><FeatureGate featureKey="nutritionist_v01"><NutritionistProfilePage /></FeatureGate></Layout>} />
          <Route path="/my-requests" element={<Layout><MyRequestsPage /></Layout>} />
          {/* Cluster C.6 — wallet UI: balance + unexpired credits + ledger. */}
          <Route path="/wallet" element={<Layout><WalletPage /></Layout>} />

          {/* Admin routes — role-gated client-side (server still authoritative). */}
          <Route path="/admin" element={<RequireRole roles={ADMIN_ROLES}><Layout><AdminDashboardPage /></Layout></RequireRole>} />
          {/* Cluster C.7 — admin refund decision panel. */}
          <Route path="/admin/refunds" element={<RequireRole roles={ADMIN_ROLES}><Layout><AdminRefundsPage /></Layout></RequireRole>} />
          {/* docs/32 consult P1 — ops console for the shortlist→choose queue. */}
          <Route path="/admin/consults" element={<RequireRole roles={ADMIN_ROLES}><Layout><AdminConsultsPage /></Layout></RequireRole>} />
          <Route path="/admin/heatmap" element={<RequireRole roles={ADMIN_ROLES}><HeatmapPage /></RequireRole>} />
          <Route path="/ai-training" element={<RequireRole roles={ADMIN_ROLES}><Layout><AITrainingPage /></Layout></RequireRole>} />

          {/* Carer routes */}
          <Route path="/carer-admin" element={<RequireRole roles={['carer', ...ADMIN_ROLES]}><Layout><CarerAdminPage /></Layout></RequireRole>} />

          {/* Customer Service routes */}
          <Route path="/cs" element={<RequireRole roles={['customer_service', ...ADMIN_ROLES]}><Layout><CSDashboardPage /></Layout></RequireRole>} />
          <Route path="/cs/checkins" element={<RequireRole roles={['customer_service', ...ADMIN_ROLES]}><Layout><CSCheckinPage /></Layout></RequireRole>} />

          {/* Property Manager routes */}
          <Route path="/pm" element={<RequireRole roles={['property_manager', ...ADMIN_ROLES]}><Layout><PMDashboardPage /></Layout></RequireRole>} />

          {/* Fallback */}
          <Route path="/feedback" element={<SurveyPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogArticlePage />} />
          <Route path="*" element={<Layout><NotFoundPage /></Layout>} />
        </>
      ) : (
        /* Unauthenticated routes */
        <>
          <Route path="/" element={<LandingPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogArticlePage />} />
          <Route path="/feedback" element={<SurveyPage />} />
          <Route path="/passport/:shareToken" element={<><SEOHead title="Shared Health Passport | nsofar" noindex /><PassportPublicPage /></>} />
          <Route path="*" element={<NotFoundPage />} />
        </>
      )}
    </Routes>
    </Suspense>
    <UpgradeModal />
  </>
  );
}

function getHomeRoute(user) {
  if (!user) return '/portal';
  const role = user.role;
  if (!user.onboarding_complete && role === 'user') return '/onboarding';
  switch (role) {
    case 'admin':
    case 'super_admin':
      return '/admin';
    case 'carer':
      return '/carer-admin';
    case 'customer_service':
      return '/cs';
    case 'property_manager':
      return '/pm';
    default:
      return '/';
  }
}

const ADMIN_ROLES = ['admin', 'super_admin'];

// Client-side role gate (audit §10 #10 / docs/37 P1). Defense-in-depth only —
// the server is the real authority on every data endpoint; this just stops a
// wrong-role account from rendering a privileged shell (e.g. a plain user
// opening the admin console). admin/super_admin are included as superusers on
// every staff surface so gating can never lock a role out of a page it can
// reach today; a disallowed role is redirected to its own home, not shown a
// scary error.
function RequireRole({ roles, children }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/auth" replace />;
  if (!roles.includes(user.role)) return <Navigate to={getHomeRoute(user)} replace />;
  return children;
}

function App() {
  return (
    <HelmetProvider>
      <ErrorBoundary>
      <BrowserRouter>
        {/* App-wide on purpose (public pages AND the signed-in app): a new
            page opens at the top; Back/Forward and query-only changes are
            left alone. See the component for the rules. */}
        <ScrollToTop />
        <AuthProvider>
          <CurrencyProvider>
            <WsProvider>
              <FeatureAccessProvider>
                <EntitlementProvider>
                  <PartnerConsentProvider>
                  <ConfirmProvider>
                    <AppRoutes />
                    {/* Cluster C.3 — global entitlement modal; mounted once,
                        opens whenever any component calls useEntitlementGate(). */}
                    <EntitlementModal />
                    {/* Cluster F.2 / Q12.5 — global just-in-time partner
                        consent modal; opens whenever any booking page
                        calls usePartnerConsentGate().check(...). */}
                    <PartnerConsentModal />
                    <CookieConsent />
                    <InstallPrompt />
                    <Toaster position="top-right" richColors closeButton duration={3000} />
                  </ConfirmProvider>
                  </PartnerConsentProvider>
                </EntitlementProvider>
              </FeatureAccessProvider>
            </WsProvider>
          </CurrencyProvider>
        </AuthProvider>
      </BrowserRouter>
      </ErrorBoundary>
    </HelmetProvider>
  );
}

export default App;
